// Autocomplete for tag search input.
export function tagSearch(opts = {}) {
  // Options with sensible fallbacks
  const {
    inputSelector = '#tag-search',
    data = null, // [{id, link, text, count}]
    maxSuggestions = undefined, // number
    i18n = undefined, // { noResults }
    attachStyles = false, // inject minimal CSS if you want
  } = opts;

  // Resolve data source: explicit `data` or window.empower_options
  const globalOpts =
    typeof window !== 'undefined' && window.empower_options ? window.empower_options : {};
  const TAGS = Array.isArray(data)
    ? data
    : Array.isArray(globalOpts.data.tags)
      ? globalOpts.data.tags
      : [];
  const MAX = 8;
  const I18N = {
    noResults:
      (i18n && i18n.noResults) || (globalOpts.i18n && globalOpts.i18n.noResults) || 'No results',
  };

  // Guard clauses
  const input = document.querySelector(inputSelector);
  if (!input || !Array.isArray(TAGS) || TAGS.length === 0) {
    return { destroy() {} }; // nothing to do
  }

  if (attachStyles) {
    injectStyles();
  }

  // Container and dropdown
  const wrap = document.createElement('div');
  wrap.className = 'empower-autocomplete-wrap';
  wrap.style.position = 'relative';
  input.parentNode.insertBefore(wrap, input);
  wrap.appendChild(input);

  const list = document.createElement('ul');
  list.className = 'empower-autocomplete';
  list.setAttribute('role', 'listbox');
  list.style.display = 'none';
  list.style.zIndex = '9999';
  wrap.appendChild(list);

  let items = [];
  let currentIndex = -1;

  // Helpers
  const sanitize = (str) => String(str || '').toLowerCase();
  const filter = (q) => {
    const s = sanitize(q);
    if (!s) return [];
    return TAGS.filter((t) => sanitize(t.text).includes(s)).slice(0, MAX);
  };
  const clearList = () => {
    list.innerHTML = '';
    list.style.display = 'none';
    items = [];
    currentIndex = -1;
  };
  const render = (results) => {
    list.innerHTML = '';
    items = [];

    if (!results.length) {
      const li = document.createElement('li');
      li.className = 'is-empty';
      li.textContent = I18N.noResults;
      li.setAttribute('role', 'option');
      li.setAttribute('aria-disabled', 'true');
      list.appendChild(li);
      list.style.display = 'block';
      return;
    }

    results.forEach((r, idx) => {
      const li = document.createElement('li');
      li.className = 'empower-autocomplete-item';
      li.setAttribute('role', 'option');
      li.dataset.index = String(idx);
      li.dataset.link = r.link;

      li.textContent = r.text;

      li.addEventListener('mouseenter', () => setActive(idx));
      li.addEventListener('mouseleave', () => removeActive(idx));
      li.addEventListener('mousedown', (ev) => {
        ev.preventDefault();
        navigateTo(r.link);
      });

      list.appendChild(li);
      items.push(li);
    });

    list.style.display = 'block';
    syncWidth();
  };
  const setActive = (idx) => {
    removeActive();
    if (idx >= 0 && idx < items.length) {
      currentIndex = idx;
      items[idx].classList.add('is-active');
      items[idx].setAttribute('aria-selected', 'true');
    }
  };
  const removeActive = (idx) => {
    if (typeof idx === 'number') {
      if (items[idx]) {
        items[idx].classList.remove('is-active');
        items[idx].removeAttribute('aria-selected');
      }
      return;
    }
    items.forEach((el) => {
      el.classList.remove('is-active');
      el.removeAttribute('aria-selected');
    });
    currentIndex = -1;
  };
  const navigateTo = (url) => {
    if (!url) return;
    window.location.href = url;
  };
  const syncWidth = () => {
    // Keep dropdown width at least the input width
    list.style.minWidth = input.offsetWidth + 'px';
  };

  // Events
  const onInput = () => {
    const q = input.value.trim();
    if (!q) {
      clearList();
      return;
    }
    render(filter(q));
  };
  const onKeydown = (ev) => {
    if (list.style.display === 'none') return;

    if (ev.key === 'ArrowDown') {
      ev.preventDefault();
      setActive(Math.min(currentIndex + 1, items.length - 1));
    } else if (ev.key === 'ArrowUp') {
      ev.preventDefault();
      setActive(Math.max(currentIndex - 1, 0));
    } else if (ev.key === 'Enter') {
      if (currentIndex >= 0 && items[currentIndex]) {
        ev.preventDefault();
        navigateTo(items[currentIndex].dataset.link);
      }
    } else if (ev.key === 'Escape') {
      clearList();
    }
  };
  const onBlur = () => {
    // Delay so mousedown on item can fire first
    setTimeout(clearList, 120);
  };
  const onResize = () => syncWidth();
  const onDocClick = (ev) => {
    if (!wrap.contains(ev.target)) clearList();
  };

  // Bind
  input.addEventListener('input', onInput);
  input.addEventListener('keydown', onKeydown);
  input.addEventListener('blur', onBlur);
  window.addEventListener('resize', onResize);
  document.addEventListener('click', onDocClick);

  // Public API
  function destroy() {
    input.removeEventListener('input', onInput);
    input.removeEventListener('keydown', onKeydown);
    input.removeEventListener('blur', onBlur);
    window.removeEventListener('resize', onResize);
    document.removeEventListener('click', onDocClick);
    clearList();
    if (list && list.parentNode) list.parentNode.removeChild(list);
    if (wrap && wrap.parentNode) {
      // put input back where it was
      wrap.parentNode.insertBefore(input, wrap);
      wrap.parentNode.removeChild(wrap);
    }
  }

  return { destroy };
}

// Optional CSS injector, off by default
function injectStyles() {
  if (document.getElementById('empower-autocomplete-styles')) return;
  const css = `
  .empower-autocomplete {
    list-style: none;
    margin: 4px 0 0;
    padding: 0;
    border: 1px solid #ddd;
    background: #fff;
    max-height: 280px;
    overflow-y: auto;
    box-shadow: 0 6px 18px rgba(0,0,0,.08);
    border-radius: 6px;
  }
  .empower-autocomplete li { padding: 8px 10px; cursor: pointer; line-height: 1.3; }
  .empower-autocomplete li.is-active { background: #f2f6ff; }
  .empower-autocomplete li.is-empty { color: #666; cursor: default; }
  `;
  const style = document.createElement('style');
  style.id = 'empower-autocomplete-styles';
  style.type = 'text/css';
  style.appendChild(document.createTextNode(css));
  document.head.appendChild(style);
}
